// Web Audio API Synthesizer for Binaural Beats and Ambient Drones
class SoundscapeEngine {
  constructor() {
    this.audioCtx = null;
    this.leftOsc = null;
    this.rightOsc = null;
    this.lfo = null;
    this.leftGain = null;
    this.rightGain = null;
    this.masterGain = null;
    this.analyser = null;
    
    this.isPlaying = false;
    this.carrierFreq = 432;
    this.offsetFreq = 4;
    this.volume = 0.5;
  }

  init() {
    if (this.audioCtx) return;

    // Create AudioContext (safely handling cross-browser prefix)
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    this.audioCtx = new AudioContextClass();

    // Create Master Gain node
    this.masterGain = this.audioCtx.createGain();
    this.masterGain.gain.setValueAtTime(this.volume, this.audioCtx.currentTime);

    // Create Analyser Node for visualizer
    this.analyser = this.audioCtx.createAnalyser();
    this.analyser.fftSize = 256;

    // Setup routing: MasterGain -> Analyser -> Destination
    this.masterGain.connect(this.analyser);
    this.analyser.connect(this.audioCtx.destination);
  }

  start() {
    this.init();

    // Resume AudioContext if suspended (browser security autoplay policies)
    if (this.audioCtx.state === 'suspended') {
      this.audioCtx.resume();
    }

    if (this.isPlaying) return;

    const t = this.audioCtx.currentTime;

    // Create Left & Right Channel Oscillators
    this.leftOsc = this.audioCtx.createOscillator();
    this.rightOsc = this.audioCtx.createOscillator();

    // Set waves to sine for maximum purity of tone (essential for binaural beats)
    this.leftOsc.type = 'sine';
    this.rightOsc.type = 'sine';

    // Set Frequencies
    this.leftOsc.frequency.setValueAtTime(this.carrierFreq, t);
    this.rightOsc.frequency.setValueAtTime(this.carrierFreq + this.offsetFreq, t);

    // Create Panning Nodes for Stereo Isolation
    const leftPanner = this.audioCtx.createStereoPanner();
    leftPanner.pan.setValueAtTime(-1, t); // Hard left

    const rightPanner = this.audioCtx.createStereoPanner();
    rightPanner.pan.setValueAtTime(1, t); // Hard right

    // Create Individual Channel Gains
    this.leftGain = this.audioCtx.createGain();
    this.rightGain = this.audioCtx.createGain();
    this.leftGain.gain.setValueAtTime(0.4, t);
    this.rightGain.gain.setValueAtTime(0.4, t);

    // LFO (Low Frequency Oscillator) to modulate volume for an organic 'breathing' texture
    this.lfo = this.audioCtx.createOscillator();
    this.lfo.frequency.setValueAtTime(0.1, t); // Very slow sweep (10 seconds per cycle)
    this.lfo.type = 'sine';

    const lfoGain = this.audioCtx.createGain();
    lfoGain.gain.setValueAtTime(0.15, t); // Modulates volume by +/- 15%

    // Route LFO: LFO -> LfoGain -> Oscillators Gains
    this.lfo.connect(lfoGain);
    lfoGain.connect(this.leftGain.gain);
    lfoGain.connect(this.rightGain.gain);

    // Main Audio Routing
    // Left Osc -> Left Panner -> Left Gain -> Master Gain
    this.leftOsc.connect(leftPanner);
    leftPanner.connect(this.leftGain);
    this.leftGain.connect(this.masterGain);

    // Right Osc -> Right Panner -> Right Gain -> Master Gain
    this.rightOsc.connect(rightPanner);
    rightPanner.connect(this.rightGain);
    this.rightGain.connect(this.masterGain);

    // Start Oscillators
    this.leftOsc.start(t);
    this.rightOsc.start(t);
    this.lfo.start(t);

    this.isPlaying = true;
  }

  stop() {
    if (!this.isPlaying) return;

    const t = this.audioCtx ? this.audioCtx.currentTime : 0;
    
    // Graceful fade out to avoid audio pops/clicks
    if (this.masterGain) {
      this.masterGain.gain.setValueAtTime(this.volume, t);
      this.masterGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.1);
    }

    setTimeout(() => {
      try {
        if (this.leftOsc) {
          this.leftOsc.stop();
          this.leftOsc.disconnect();
        }
        if (this.rightOsc) {
          this.rightOsc.stop();
          this.rightOsc.disconnect();
        }
        if (this.lfo) {
          this.lfo.stop();
          this.lfo.disconnect();
        }
        this.leftOsc = null;
        this.rightOsc = null;
        this.lfo = null;
        this.isPlaying = false;
        
        // Restore volume value on master node for future playback
        if (this.masterGain) {
          this.masterGain.gain.setValueAtTime(this.volume, this.audioCtx.currentTime);
        }
      } catch (e) {
        console.warn("Audio stop warning:", e);
      }
    }, 120);
  }

  setCarrier(frequency) {
    this.carrierFreq = parseFloat(frequency);
    if (this.isPlaying && this.leftOsc && this.rightOsc) {
      const t = this.audioCtx.currentTime;
      this.leftOsc.frequency.exponentialRampToValueAtTime(this.carrierFreq, t + 0.2);
      this.rightOsc.frequency.exponentialRampToValueAtTime(this.carrierFreq + this.offsetFreq, t + 0.2);
    }
  }

  setOffset(offset) {
    this.offsetFreq = parseFloat(offset);
    if (this.isPlaying && this.rightOsc) {
      const t = this.audioCtx.currentTime;
      this.rightOsc.frequency.exponentialRampToValueAtTime(this.carrierFreq + this.offsetFreq, t + 0.2);
    }
  }

  setVolume(vol) {
    this.volume = parseFloat(vol);
    if (this.masterGain) {
      const t = this.audioCtx.currentTime;
      this.masterGain.gain.linearRampToValueAtTime(this.volume, t + 0.1);
    }
  }

  // Retrieve analyzer byte frequency data for visualizer drawing
  getVisualizerData() {
    if (!this.analyser || !this.isPlaying) return null;
    const bufferLength = this.analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    this.analyser.getByteTimeDomainData(dataArray);
    return dataArray;
  }
}

export const soundEngine = new SoundscapeEngine();
